import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { format } from "date-fns";
import "../../assets/scss/custom/_careJourneyRoadmap.scss";
import { useSelector } from "react-redux";
import { UseCareJourneyResult, useCareJourney } from "../../hooks/useCareJourney";
import { RootState } from "../../store";
import {
  CareJourneyRoadmapForm,
  CareJourneyRoadmapStep,
  CareJourneyStatus,
} from "../../api-helpers/CareJourneyTypes";
import { CareJourneyRoadmapSkeleton } from "./CareJourneyRoadmapSkeleton";

interface CareJourneyRoadmapProps {
  patientId: string;
  /**
   * A band heading above the card, as on the patient dashboard ("Care journey").
   * Inside the component so it hides with the card for RPM Only patients.
   */
  heading?: string;
  /** A title inside the card, top left, as on the appointment page ("Care Journey"). */
  title?: string;
  /**
   * The journey, when the page has already loaded it (the appointment page does,
   * so it can reveal everything at once). Without it the roadmap loads its own.
   */
  journey?: UseCareJourneyResult;
}

const FORM_STATUS_LABEL: Record<CareJourneyStatus, string> = {
  completed: "Completed",
  "in-progress": "In progress",
  "not-started": "Not started",
  unavailable: "Coming soon",
};

const formatDate = (value: string | null) =>
  value ? format(new Date(value), "MMM d, yyyy") : null;

// "Module 2: Respiratory Medications" -> eyebrow "Module 2", title
// "Respiratory Medications", so long module names don't wrap three lines.
const splitStepName = (name: string) => {
  const match = /^(Module \d+):\s*(.+)$/.exec(name);
  return match
    ? { eyebrow: match[1], title: match[2] }
    : { eyebrow: null, title: name };
};

// One-line status for a step. Only "Next up" is shown on the track; the rest
// lives in the tooltip and screen-reader text so the track stays compact.
const stepStatusText = (step: CareJourneyRoadmapStep, isCurrent: boolean) => {
  if (isCurrent) return "Next up";
  switch (step.status) {
    case "completed": {
      const completedOn = formatDate(step.completedAt);
      return completedOn ? `Completed ${completedOn}` : "Completed";
    }
    case "in-progress":
      return "In progress";
    case "unavailable":
      return "Coming soon";
    default:
      return step.isOptional ? "Optional" : "Not started";
  }
};

const FORM_STATUS_ICON: Record<CareJourneyStatus, string> = {
  completed: "✓",
  "in-progress": "◐",
  "not-started": "○",
  unavailable: "○",
};

const toneFor = (step: CareJourneyRoadmapStep, isCurrent: boolean) =>
  isCurrent ? "current" : step.status;

// Header (name + colour-coded status) then one row per form: icon, name, and
// the submitted date or status right-aligned.
const StepTooltip = ({
  step,
  statusText,
  isCurrent,
}: {
  step: CareJourneyRoadmapStep;
  statusText: string;
  isCurrent: boolean;
}) => (
  <div className="care-journey-tip">
    <div className="care-journey-tip__title">{step.name}</div>
    <div
      className={`care-journey-tip__status care-journey-tip__status--${toneFor(
        step,
        isCurrent
      )}`}
    >
      {statusText}
      {step.status === "in-progress" && step.forms.length > 1 && (
        <span className="care-journey-tip__count">
          {" · "}
          {step.forms.filter((form) => form.status === "completed").length} of{" "}
          {step.forms.length} done
        </span>
      )}
      {step.isOptional && (
        <span className="care-journey-tip__optional"> · Optional, if applicable</span>
      )}
    </div>
    {step.status !== "unavailable" && step.forms.length > 1 && (
      <ul className="care-journey-tip__forms">
        {step.forms.map((form: CareJourneyRoadmapForm) => (
          <li
            key={form.clinicalFormId}
            className={`care-journey-tip__form care-journey-tip__form--${form.status}`}
          >
            <span className="care-journey-tip__icon" aria-hidden="true">
              {FORM_STATUS_ICON[form.status]}
            </span>
            <span className="care-journey-tip__form-name">{form.name}</span>
            <span className="care-journey-tip__form-meta">
              {formatDate(form.submittedAt) ?? FORM_STATUS_LABEL[form.status]}
            </span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const CheckIcon = () => (
  <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
    <path
      d="M3.5 8.5l3 3 6-7"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const NextIcon = () => (
  <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
    <path
      d="M3.5 8h8.5M8.5 4.5L12 8l-3.5 3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Roadmap of the patient's care journey: each step's progress and the step
// the clinician should work on next. Adapts to the card's width (container
// query in _careJourneyRoadmap.scss): a labelled track when wide, a segmented
// bar when narrow.
//
// Always loads in the background, but only shows once the patient header
// (always above this, on the profile and its appointment pages) has reported
// the patient isn't RPM Only -- so RPM Only patients never see it flash in, and
// flipping the header's toggle shows or hides it at once.
export const CareJourneyRoadmap = ({
  patientId,
  heading,
  title,
  journey,
}: CareJourneyRoadmapProps) => {
  const headerRpmOnly = useSelector(
    (state: RootState): boolean | undefined =>
      state.patientRpmOnly.byPatientId[patientId]
  );
  // A missing patient id makes the hook skip its fetch.
  const ownJourney = useCareJourney(journey ? undefined : patientId);
  const { status, roadmap, errorMessage } = journey ?? ownJourney;
  if (headerRpmOnly !== false || (status === "loaded" && !roadmap)) return null;
  if (status === "loading") {
    return <CareJourneyRoadmapSkeleton heading={heading} title={title} />;
  }

  const completedCount =
    roadmap?.steps.filter((s) => s.status === "completed").length ?? 0;
  const trackedCount =
    roadmap?.steps.filter((s) => s.status !== "unavailable").length ?? 0;
  const currentIndex =
    roadmap?.steps.findIndex((s) => s.id === roadmap.currentStepId) ?? -1;

  const card = (
    <div className="care-journey">
      {(title || roadmap) && (
        <div className="care-journey__header">
          {title && <h3 className="care-journey__title">{title}</h3>}
          {roadmap && (
            <span className="care-journey__summary">
              {completedCount} of {trackedCount} complete
            </span>
          )}
        </div>
      )}

      {status === "error" && (
        <div className="care-journey__message text-danger">{errorMessage}</div>
      )}

      {status === "loaded" && roadmap && (
        <>
          <ol className="care-journey__track" aria-label="Patient journey">
            {roadmap.steps.map((step, index) => {
              const isCurrent = index === currentIndex;
              const { eyebrow, title } = splitStepName(step.name);
              const statusText = stepStatusText(step, isCurrent);
              return (
                <li
                  key={step.id}
                  className={[
                    "care-journey__step",
                    `care-journey__step--${step.status}`,
                    isCurrent ? "care-journey__step--current" : "",
                  ].join(" ")}
                  data-testid={`care-journey-step-${step.id}`}
                  aria-current={isCurrent ? "step" : undefined}
                >
                  <OverlayTrigger
                    placement="bottom"
                    overlay={
                      <Tooltip
                        id={`care-journey-tip-${step.id}`}
                        className="care-journey-tooltip"
                      >
                        <StepTooltip
                          step={step}
                          statusText={statusText}
                          isCurrent={isCurrent}
                        />
                      </Tooltip>
                    }
                  >
                    <div className="care-journey__step-inner" tabIndex={0}>
                      <span className="care-journey__node-wrap" aria-hidden="true">
                        <span className="care-journey__node">
                          {isCurrent ? (
                            <NextIcon />
                          ) : (
                            step.status === "completed" && <CheckIcon />
                          )}
                        </span>
                      </span>
                      <span className="care-journey__text">
                        {/* Blank eyebrow keeps names aligned across steps. */}
                        <span className="care-journey__eyebrow">
                          {eyebrow ?? "\u00a0"}
                        </span>
                        <span className="care-journey__label">{title}</span>
                      </span>
                      {isCurrent ? (
                        <span className="care-journey__pill">Next up</span>
                      ) : step.isOptional && step.status !== "completed" ? (
                        <span className="care-journey__pill care-journey__pill--optional">
                          Optional
                        </span>
                      ) : (
                        <span className="visually-hidden">{statusText}</span>
                      )}
                    </div>
                  </OverlayTrigger>
                </li>
              );
            })}
          </ol>

          <div
            className="care-journey__segments"
            role="img"
            aria-label={`${completedCount} of ${trackedCount} steps complete`}
          >
            {roadmap.steps.map((step, index) => (
              <span
                key={step.id}
                className={[
                  "care-journey__segment",
                  `care-journey__segment--${step.status}`,
                  index === currentIndex ? "care-journey__segment--current" : "",
                ].join(" ")}
                title={`${step.name}: ${stepStatusText(step, index === currentIndex)}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );

  return heading ? (
    <div>
      <h2 className="ps-band-title">{heading}</h2>
      {card}
    </div>
  ) : (
    card
  );
};
